import React, { useState, useEffect } from "react";
import "../AddFareRule/AddFareRule.css";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import Loader from "../../Components/Loader/Loader";
import ChipInput from "../../Components/ChipInput/ChipInput";

const BASE_URL = "https://api.aeroprime.in/airline-service";
const CRM_BASE_URL = "https://api.aeroprime.in/crm-service";

const MARKUP_TYPES = ["PERCENTAGE", "FIXED"];
const TRIP_TYPES = ["ONE_WAY", "ROUND_TRIP"];
const PASSENGER_TYPES = ["ADLT", "CHLD", "INFT"];

const getHeaders = () => ({
  Authorization: localStorage.getItem("AuthToken"),
  Accept: "application/json",
});

const emptyCriteria = () => ({
  origin: [],
  destination: [],
  cabin: [],
  fare_reference_code: [],
  resBookDesigCode: [],
  resBookDesigStatusCode: [],
  fareGroupName: [],
  passenger_type: [],
  trip_type: [],
});

const CriteriaFields = ({ criteria, onChange, idPrefix }) => {
  const set = (field) => (values) => onChange({ ...criteria, [field]: values });

  return (
    <div className="form-row">
      <div className="form-field">
        <label htmlFor={`${idPrefix}-origin`}>Origin</label>
        <ChipInput values={criteria.origin} onChange={set("origin")} placeholder="Type & Enter (e.g. DEL)" />
      </div>
      <div className="form-field">
        <label htmlFor={`${idPrefix}-destination`}>Destination</label>
        <ChipInput values={criteria.destination} onChange={set("destination")} placeholder="Type & Enter (e.g. LGW)" />
      </div>
      <div className="form-field">
        <label htmlFor={`${idPrefix}-cabin`}>Cabin</label>
        <ChipInput values={criteria.cabin} onChange={set("cabin")} placeholder="Type & Enter (e.g. Economy)" normalizeValue={(v) => v.trim()} />
      </div>
      <div className="form-field">
        <label htmlFor={`${idPrefix}-fare-ref`}>Fare Reference Code</label>
        <ChipInput values={criteria.fare_reference_code} onChange={set("fare_reference_code")} placeholder="Type & Enter (e.g. GOWFRN)" />
      </div>
      <div className="form-field">
        <label htmlFor={`${idPrefix}-rbd`}>Booking Class (RBD)</label>
        <ChipInput values={criteria.resBookDesigCode} onChange={set("resBookDesigCode")} placeholder="Type & Enter (e.g. G)" />
      </div>
      <div className="form-field">
        <label htmlFor={`${idPrefix}-rbd-status`}>RBD Status Code</label>
        <ChipInput values={criteria.resBookDesigStatusCode} onChange={set("resBookDesigStatusCode")} placeholder="Type & Enter (e.g. ECONOMY:O:G)" normalizeValue={(v) => v.trim()} />
      </div>
      <div className="form-field">
        <label htmlFor={`${idPrefix}-fare-group`}>Fare Group Name</label>
        <ChipInput values={criteria.fareGroupName} onChange={set("fareGroupName")} placeholder="Type & Enter (e.g. ECO LIGHT)" normalizeValue={(v) => v.trim().toUpperCase()} />
      </div>
      <div className="form-field">
        <label htmlFor={`${idPrefix}-pax`}>Passenger Type</label>
        <ChipInput values={criteria.passenger_type} onChange={set("passenger_type")} placeholder="Select passenger type" options={PASSENGER_TYPES} />
      </div>
      <div className="form-field">
        <label htmlFor={`${idPrefix}-trip`}>Trip Type</label>
        <ChipInput values={criteria.trip_type} onChange={set("trip_type")} placeholder="Select trip type" options={TRIP_TYPES} />
      </div>
    </div>
  );
};

const toClientOptions = (data) => {
  if (!data || typeof data !== "object") return [];
  const entries = Array.isArray(data)
    ? data.map((c) => [String(c.clientId), c.clientName])
    : Object.keys(data).map((key) => [key, data[key]]);
  return entries
    .filter(([clientId]) => clientId !== "0")
    .map(([clientId, clientName]) => ({ clientId, clientName }))
    .sort((a, b) => String(a.clientName).localeCompare(String(b.clientName)));
};

const AddMarkupRule = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editingRuleId = searchParams.get("ruleId");

  const [showLoader, setShowLoader] = useState(false);
  const [clients, setClients] = useState([]);

  const [ruleId, setRuleId] = useState("");
  const [markupValue, setMarkupValue] = useState("");
  const [markupType, setMarkupType] = useState("PERCENTAGE");
  const [clientId, setClientId] = useState("");
  const [airlineCode, setAirlineCode] = useState("");
  const [priority, setPriority] = useState("0");
  const [enabled, setEnabled] = useState(true);
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [effectiveTo, setEffectiveTo] = useState("");
  const [travelFrom, setTravelFrom] = useState("");
  const [travelTo, setTravelTo] = useState("");
  const [inclusion, setInclusion] = useState(emptyCriteria());
  const [exclusion, setExclusion] = useState(emptyCriteria());

  useEffect(() => {
    axios
      .get(`${CRM_BASE_URL}/client/details`, { headers: getHeaders() })
      .then((res) => setClients(toClientOptions(res.data)))
      .catch(() => setClients([]));
  }, []);

  useEffect(() => {
    if (!editingRuleId) return;
    setShowLoader(true);
    axios
      .get(`${BASE_URL}/admin/markupRule/get/${editingRuleId}`, { headers: getHeaders() })
      .then((res) => {
        setShowLoader(false);
        const rule = res.data;
        if (!rule) return;
        setRuleId(rule.ruleId || "");
        setMarkupValue(rule.markupValue !== undefined ? String(rule.markupValue) : "");
        setMarkupType(rule.markupType || "PERCENTAGE");
        setClientId(rule.clientId != null ? String(rule.clientId) : "");
        setAirlineCode(rule.airlineCode || "");
        setPriority(rule.priority != null ? String(rule.priority) : "0");
        setEnabled(rule.enabled !== false);
        setEffectiveFrom(rule.effectiveFrom || "");
        setEffectiveTo(rule.effectiveTo || "");
        setTravelFrom(rule.travelFrom || "");
        setTravelTo(rule.travelTo || "");
        const inclusionParsed = safeParse(rule.inclusionCriteria);
        const exclusionParsed = safeParse(rule.exclusionCriteria);
        if (!rule.airlineCode && Array.isArray(inclusionParsed.airline_code)
            && inclusionParsed.airline_code.length) {
          setAirlineCode(inclusionParsed.airline_code[0]);
        }
        delete inclusionParsed.airline_code;
        delete exclusionParsed.airline_code;
        setInclusion({ ...emptyCriteria(), ...inclusionParsed });
        setExclusion({ ...emptyCriteria(), ...exclusionParsed });
      })
      .catch(() => setShowLoader(false));
  }, [editingRuleId]);

  const safeParse = (value) => {
    if (!value) return {};
    try {
      return JSON.parse(value);
    } catch (e) {
      return {};
    }
  };

  const stripEmpty = (criteria) => {
    const result = {};
    Object.keys(criteria).forEach((key) => {
      if (Array.isArray(criteria[key]) && criteria[key].length > 0) {
        result[key] = criteria[key];
      }
    });
    return result;
  };

  const handleSave = () => {
    if (!ruleId.trim()) {
      alert("Rule ID is required.");
      return;
    }
    if (markupValue === "" || isNaN(Number(markupValue)) || Number(markupValue) < 0) {
      alert("Markup value is required and cannot be negative.");
      return;
    }
    const inclusionPayload = stripEmpty(inclusion);
    if (!airlineCode.trim()) {
      alert("Airline code is required.");
      return;
    }
    if (effectiveFrom && effectiveTo && effectiveTo < effectiveFrom) {
      alert("Booking window end cannot be before its start.");
      return;
    }
    if (travelFrom && travelTo && travelTo < travelFrom) {
      alert("Travel window end cannot be before its start.");
      return;
    }

    const payload = {
      rule_id: ruleId.trim(),
      markupValue: Number(markupValue),
      markup_type: markupType,
      clientId: clientId === "" ? null : Number(clientId),
      airlineCode: airlineCode.trim(),
      priority: priority === "" ? 0 : parseInt(priority, 10),
      enabled,
      effective_from: effectiveFrom || null,
      effective_to: effectiveTo || null,
      travel_from: travelFrom || null,
      travel_to: travelTo || null,
      inclusion_criteria: inclusionPayload,
      exclusion_criteria: stripEmpty(exclusion),
    };

    setShowLoader(true);
    axios
      .post(`${BASE_URL}/admin/markupRule/saveUpdate`, payload, { headers: getHeaders() })
      .then((res) => {
        setShowLoader(false);
        if (res.data?.success) {
          navigate("/markupRules");
        } else {
          alert("Failed to save: " + (res.data?.message || "Unknown error"));
        }
      })
      .catch((err) => {
        setShowLoader(false);
        if (err.response?.status === 401) {
          localStorage.clear();
          window.location.href = "/";
        } else {
          alert("Failed to save markup rule.");
        }
      });
  };

  return (
    <div className="add-fare-rule-wrapper">
      {showLoader && <Loader />}
      <h2 className="add-fare-rule-heading">{editingRuleId ? "Edit Markup Rule" : "Add Markup Rule"}</h2>
      <p className="baggage-subtitle">
        A rule replaces the base markup only for fares that match every criteria below. Leave a
        field empty to let it match anything.
      </p>

      <div className="form-section">
        <h3 className="form-section-title">Rule</h3>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor="rule-id">Rule ID *</label>
            <input
              id="rule-id"
              value={ruleId}
              disabled={!!editingRuleId}
              onChange={(e) => setRuleId(e.target.value)}
              placeholder="e.g. T5_DEL_LGW_OCT"
            />
          </div>
          <div className="form-field">
            <label htmlFor="markup-value">Markup Value *</label>
            <input
              id="markup-value"
              type="number"
              min="0"
              value={markupValue}
              onChange={(e) => setMarkupValue(e.target.value)}
              placeholder={markupType === "FIXED" ? "e.g. 500" : "e.g. 25"}
            />
          </div>
          <div className="form-field">
            <label htmlFor="markup-type">Markup Type</label>
            <select id="markup-type" value={markupType} onChange={(e) => setMarkupType(e.target.value)}>
              {MARKUP_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type === "PERCENTAGE"
                    ? "Percentage of base fare (%)"
                    : "Fixed amount per passenger (airline fare currency)"}
                </option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label htmlFor="client-id">Client</label>
            <select id="client-id" value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">All clients</option>
              {clients.map((client) => (
                <option key={client.clientId} value={client.clientId}>
                  {client.clientName}
                </option>
              ))}
            </select>
          </div>
          <div className="form-field">
            <label htmlFor="airline-code">Airline Code *</label>
            <input
              id="airline-code"
              value={airlineCode}
              onChange={(e) => setAirlineCode(e.target.value.toUpperCase())}
              placeholder="e.g. T5"
            />
          </div>
          <div className="form-field">
            <label htmlFor="priority">Priority</label>
            <input
              id="priority"
              type="number"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              placeholder="Higher wins, e.g. 100"
            />
          </div>
          <div className="form-field">
            <label htmlFor="enabled">Status</label>
            <select id="enabled" value={enabled ? "true" : "false"} onChange={(e) => setEnabled(e.target.value === "true")}>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3 className="form-section-title">Booking Window</h3>
        <p className="baggage-subtitle">Matched against today's date, i.e. when the search or booking happens.</p>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor="effective-from">Effective From</label>
            <input id="effective-from" type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
          </div>
          <div className="form-field">
            <label htmlFor="effective-to">Effective To</label>
            <input id="effective-to" type="date" value={effectiveTo} onChange={(e) => setEffectiveTo(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3 className="form-section-title">Travel Window</h3>
        <p className="baggage-subtitle">Matched against the departure date of the first leg.</p>
        <div className="form-row">
          <div className="form-field">
            <label htmlFor="travel-from">Travel From</label>
            <input id="travel-from" type="date" value={travelFrom} onChange={(e) => setTravelFrom(e.target.value)} />
          </div>
          <div className="form-field">
            <label htmlFor="travel-to">Travel To</label>
            <input id="travel-to" type="date" value={travelTo} onChange={(e) => setTravelTo(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3 className="form-section-title">Inclusion Criteria</h3>
        <CriteriaFields criteria={inclusion} onChange={setInclusion} idPrefix="incl" />
      </div>

      <div className="form-section">
        <h3 className="form-section-title">Exclusion Criteria</h3>
        <p className="baggage-subtitle">The rule is skipped when every field filled in here matches the fare.</p>
        <CriteriaFields criteria={exclusion} onChange={setExclusion} idPrefix="excl" />
      </div>

      <div className="form-actions">
        <button className="cancel-btn" onClick={() => navigate("/markupRules")}>Cancel</button>
        <button className="save-btn" onClick={handleSave}>
          {editingRuleId ? "Update Rule" : "Save Rule"}
        </button>
      </div>
    </div>
  );
};

export default AddMarkupRule;
